import Markdown, { ReactRenderer } from 'marked-react';
import React from 'react';
import { FOCUS_RING } from '../focusRing';
import CodeText from './CodeText';
import PageTitle from './PageTitle';
import SectionTitle from './SectionTitle';
import type { FontSize, TextColor } from './Text';
import { getFontSize, getTextColor } from './textStyles';

export type MarkdownVariant = 'block' | 'flow';

export interface MarkdownProps {
    text: string;
    color?: TextColor;
    size?: FontSize;
    omitMargin?: boolean;
    variant?: MarkdownVariant;
    normalizeSource?: boolean;
}

const FLOW_BULLET = '• ';

// Third-party content, and React does not sanitize href — `javascript:` would execute.
const ALLOWED_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

const isSafeHref = (href: unknown): href is string => {
    if (typeof href !== 'string') {
        return false;
    }
    const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(href.replace(/[\u0000-\u0020]/g, ''));
    // No scheme means relative or fragment, which has nothing to vet.
    return !scheme || ALLOWED_SCHEMES.has(`${scheme[1].toLowerCase()}:`);
};

// Renderer args are always arrays: never unwrap with `x[0]`, it drops all tokens after the first.
const getRendererFunction = ({
    omitMargin,
}: Pick<MarkdownProps, 'omitMargin'>): Partial<ReactRenderer> => ({
    blockquote(this: ReactRenderer, quote) {
        return (
            <div
                key={this.elementId}
                style={{
                    backgroundColor: 'var(--malachite-card-dimmed-background)',
                    padding: '20px',
                    borderRadius: '20px',
                    border: '1px solid var(--malachite-card-border)',
                    margin: '16px 0',
                }}
            >
                {quote}
            </div>
        );
    },

    paragraph(this: ReactRenderer, text) {
        return (
            <div key={this.elementId} style={{ marginBottom: omitMargin ? '0px' : '16px' }}>
                {text}
            </div>
        );
    },

    codespan(this: ReactRenderer, code) {
        return (
            <CodeText key={this.elementId} variant="inline">
                {code}
            </CodeText>
        );
    },

    code(this: ReactRenderer, code) {
        return (
            <pre key={this.elementId} style={{ margin: omitMargin ? 0 : '16px 0' }}>
                <CodeText
                    style={{
                        display: 'block',
                        whiteSpace: 'pre',
                        overflowX: 'auto',
                        padding: '12px',
                    }}
                >
                    {code}
                </CodeText>
            </pre>
        );
    },

    heading(this: ReactRenderer, text, level) {
        return level === 1 ? (
            <PageTitle key={this.elementId}>{text}</PageTitle>
        ) : (
            <SectionTitle key={this.elementId}>{text}</SectionTitle>
        );
    },

    br(this: ReactRenderer) {
        return <br key={this.elementId} />;
    },

    // `text`/`strong` are deliberately NOT overridden: a wrapping Text emits a font-weight, and
    // the nested 400 beat strong's 600 — which is why bold never rendered.

    link(this: ReactRenderer, href, children) {
        // Keep the label for a rejected scheme rather than deleting content.
        if (!isSafeHref(href)) {
            return <React.Fragment key={this.elementId}>{children}</React.Fragment>;
        }
        return (
            // Hover is in tailwind.css: CLAUDE.md forbids JS for styling, and JS misses keyboard.
            // FOCUS_RING because a colour change alone is not a focus indicator.
            <a
                key={this.elementId}
                className={`md-link rounded-xs ${FOCUS_RING}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
            >
                {children}
            </a>
        );
    },

    // Overridden only to vet `src`. Alt text stands in for a rejected image.
    image(this: ReactRenderer, src, alt, title) {
        if (!isSafeHref(src)) {
            return <span key={this.elementId}>{alt ?? ''}</span>;
        }
        return (
            <img
                key={this.elementId}
                src={src}
                alt={alt ?? ''}
                title={title ?? undefined}
                style={{ maxWidth: '100%' }}
            />
        );
    },

    // Preflight resets list-style/margin/padding; padding-left on the LIST is the hanging indent.
    list(this: ReactRenderer, children, ordered, start) {
        const Tag = ordered ? 'ol' : 'ul';
        return (
            <Tag
                key={this.elementId}
                // Forward `start` or `3. three` silently renumbers from 1.
                start={ordered && start !== 1 ? start : undefined}
                style={{
                    listStyle: ordered ? 'decimal' : 'disc',
                    paddingLeft: '1.25em',
                    margin: 0,
                }}
            >
                {children}
            </Tag>
        );
    },

    listItem(this: ReactRenderer, children) {
        return (
            // Padding too: it pushes line boxes off ClampText's line grid, slicing the last line.
            <li key={this.elementId} style={{ padding: omitMargin ? '0px' : '2px 0px' }}>
                {children}
            </li>
        );
    },
});

/** Turns every block-level node inline for `flow`. Every block renderer must appear here, or the
 *  one invariant — no block children — breaks silently. */
const getFlowOverrides = (): Partial<ReactRenderer> => ({
    paragraph(this: ReactRenderer, text) {
        return <span key={this.elementId}>{text} </span>;
    },

    // Roles are pinned: without the real <h*> the heading leaves the a11y tree entirely.
    heading(this: ReactRenderer, text, level) {
        return (
            <span
                key={this.elementId}
                role="heading"
                aria-level={level}
                style={{ fontWeight: 600 }}
            >
                {text}{' '}
            </span>
        );
    },

    // The list owns markers and breaks — ordered numbering needs `start` plus the item index.
    // A <br> opens a line box (what -webkit-line-clamp counts); `display: block` would not.
    // Items OPEN with it, so tailwind.css suppresses the leading one on a first-child list.
    list(this: ReactRenderer, children, ordered, start) {
        const items = React.Children.toArray(children);
        return (
            <span key={this.elementId} role="list" style={{ display: 'inline' }}>
                {items.map((item, index) => (
                    // Index as key: one list node's rendered children never reorder.
                    <span key={index} role="listitem" style={{ display: 'inline' }}>
                        <br />
                        {/* Decoration — the role already conveys position. */}
                        <span aria-hidden="true">
                            {ordered ? `${(start ?? 1) + index}. ` : FLOW_BULLET}
                        </span>
                        {item}
                    </span>
                ))}
                {/* Closes the list so following prose starts on its own line. */}
                <br />
            </span>
        );
    },

    listItem(this: ReactRenderer, children) {
        return <React.Fragment key={this.elementId}>{children}</React.Fragment>;
    },

    blockquote(this: ReactRenderer, quote) {
        return <span key={this.elementId}>{quote} </span>;
    },

    // A hard break here is incidental wrapping; honouring it spends a clamped line on nothing.
    br(this: ReactRenderer) {
        return <span key={this.elementId}> </span>;
    },

    // Whitespace collapses, so a multi-line snippet becomes one run instead of the whole budget.
    code(this: ReactRenderer, code) {
        return (
            <React.Fragment key={this.elementId}>
                <CodeText variant="inline">{code}</CodeText>{' '}
            </React.Fragment>
        );
    },

    hr(this: ReactRenderer) {
        return <span key={this.elementId}> </span>;
    },

    // Flattened to cell text: rows would each cost a line while carrying little at preview width.
    table(this: ReactRenderer, children) {
        return <span key={this.elementId}>{children} </span>;
    },
    tableHeader(this: ReactRenderer, children) {
        return <span key={this.elementId}>{children}</span>;
    },
    tableBody(this: ReactRenderer, children) {
        return <span key={this.elementId}>{children}</span>;
    },
    tableRow(this: ReactRenderer, children) {
        return <span key={this.elementId}>{children}</span>;
    },
    tableCell(this: ReactRenderer, children) {
        return <span key={this.elementId}>{children} </span>;
    },

    // Replaced content grows the line box and breaks the grid the clamp measures against.
    checkbox(this: ReactRenderer, checked) {
        return <span key={this.elementId}>{checked ? '[x] ' : '[ ] '}</span>;
    },

    image(this: ReactRenderer, _src, alt) {
        return <span key={this.elementId}>{alt ? `${alt} ` : ''}</span>;
    },
});

const dedent = (str: string) => {
    const lines = str.split('\n');
    const indents = lines.reduce<number[]>((acc, line) => {
        if (line.trim() !== '') {
            acc.push(line.match(/^[ \t]*(?=\S)/gm)?.[0].length ?? 0);
        }
        return acc;
    }, []);
    // Blank-only input has no indents to measure, and Math.min() would return Infinity — which
    // reads as a literal `{Infinity}` quantifier today, but throws the moment the `u` flag is added.
    if (indents.length === 0) {
        return str;
    }
    const indent = Math.min(...indents);
    return lines.map((line) => line.replace(new RegExp(`^[ \\t]{${indent}}`, 'gm'), '')).join('\n');
};

type BlockKind = 'blank' | 'item' | 'label' | 'prose';

const FENCE = /^ {0,3}(?:```|~~~)/;
/** Lazy continuation or indented code — either way not a new block of a different kind. */
const INDENTED = /^ {2,}\S/;
/** Belongs to the line ABOVE; a break between them demotes the heading to a thematic rule. */
const SETEXT_UNDERLINE = /^ {0,3}(?:=+|-+)\s*$/;
/** Tested before the bullet rule so `---` is not read as a list item. */
const THEMATIC_BREAK = /^ {0,3}(?:(?:-\s*){3,}|(?:\*\s*){3,}|(?:_\s*){3,})$/;
const BULLET_MARKER = /^[-*+]\s/;
const ORDERED_MARKER = /^(\d{1,9})[.)]\s/;
/** The colon is REQUIRED: emphasis mid-sentence looks identical, and splitting it would turn
 *  one paragraph into three. */
const BOLD_LABEL = /^\*\*[^*]+:\*\*$|^\*\*[^*]+\*\*:$/;

const INLINE_LABEL = /\*\*[^*\n]+?:\*\*|\*\*[^*\n]+?\*\*:/g;
const LEADING_MARKER = /^\s*(?:[-*+]|\d{1,9}[.)])\s+/;

/** Splits a bold label that TRAILS other text onto its own line — `...support. **Pagination
 *  Mode:**`. Line classification alone cannot fix it: the whole line is prose. A label that
 *  OPENS its line, or a list item, is a lead-in and left alone. */
const splitTrailingLabels = (line: string): string[] => {
    const marker = LEADING_MARKER.exec(line)?.[0] ?? '';
    let rest = line.slice(marker.length);
    const out: string[] = [];
    let isFirst = true;

    while (true) {
        INLINE_LABEL.lastIndex = 0;
        let found: RegExpExecArray | null = null;
        let match = INLINE_LABEL.exec(rest);
        while (match) {
            // index 0 means the label opens the chunk — a lead-in, not a new block.
            if (match.index > 0) {
                found = match;
                break;
            }
            match = INLINE_LABEL.exec(rest);
        }
        if (!found) {
            break;
        }
        const before = rest.slice(0, found.index).trimEnd();
        if (before) {
            out.push((isFirst ? marker : '') + before);
            isFirst = false;
        }
        rest = rest.slice(found.index);
    }

    out.push((isFirst ? marker : '') + rest);
    return out;
};

const classifyLine = (line: string, previous: BlockKind): BlockKind => {
    const trimmed = line.trim();
    if (!trimmed) {
        return 'blank';
    }
    if (THEMATIC_BREAK.test(line) || SETEXT_UNDERLINE.test(line)) {
        // Continues what came before, so nothing is inserted above it.
        return previous === 'blank' ? 'prose' : previous;
    }
    if (BULLET_MARKER.test(trimmed)) {
        return 'item';
    }
    const ordered = ORDERED_MARKER.exec(trimmed);
    if (ordered) {
        // Only a list starting at 1 may interrupt a paragraph, else "Released in\n2024." would
        // become `<ol start="2024">`.
        if (previous !== 'prose' || ordered[1] === '1') {
            return 'item';
        }
    }
    if (BOLD_LABEL.test(trimmed)) {
        return 'label';
    }
    return 'prose';
};

/** Inserts the blank line CommonMark needs between blocks of different kinds — generated
 *  descriptions separate them with a single `\n`, which reads as lazy continuation. Only a
 *  CHANGE of kind breaks, so tight lists and wrapped paragraphs survive. Skips fences, indented
 *  lines and setext underlines, where a blank line changes meaning. */
const explicitBlocks = (text: string): string => {
    const lines = text.split('\n');
    const out: string[] = [];
    let previous: BlockKind = 'blank';
    let inFence = false;

    for (const line of lines) {
        if (FENCE.test(line)) {
            inFence = !inFence;
            out.push(line);
            // A fence is its own block on both sides.
            previous = 'blank';
            continue;
        }
        if (inFence) {
            out.push(line);
            continue;
        }
        if (INDENTED.test(line)) {
            out.push(line);
            continue;
        }
        // One line can hold a block AND a trailing label, so split before classifying.
        for (const piece of splitTrailingLabels(line)) {
            previous = pushClassified(piece, previous, out);
        }
    }
    return out.join('\n');
};

const pushClassified = (line: string, previous: BlockKind, out: string[]): BlockKind => {
    const kind = classifyLine(line, previous);
    if (kind !== 'blank' && previous !== 'blank' && kind !== previous) {
        out.push('');
    }
    out.push(line);
    return kind;
};

const MarkdownBlock: React.FC<MarkdownProps> = ({
    text,
    color,
    size,
    omitMargin,
    variant = 'block',
    normalizeSource = false,
}) => {
    // Hooks before the early return so hook order stays stable. marked-react re-lexes on every
    // render, and this can land in a virtualized Table cell.
    const source = React.useMemo(
        () => (normalizeSource ? explicitBlocks(dedent(text ?? '')) : dedent(text ?? '')),
        [text, normalizeSource],
    );
    const renderer = React.useMemo(
        () =>
            variant === 'flow'
                ? { ...getRendererFunction({ omitMargin }), ...getFlowOverrides() }
                : getRendererFunction({ omitMargin }),
        [omitMargin, variant],
    );

    if (!text) {
        return null;
    }

    // `display: contents` generates no box, so this only propagates colour and size — a real box
    // would be a block child for -webkit-line-clamp to count, and a stray div inside Alert. The
    // tag still switches: `display` does not change content-model validity.
    const Root = variant === 'flow' ? 'span' : 'div';
    return (
        <Root
            className={variant === 'flow' ? 'md-root md-flow' : 'md-root'}
            style={{
                display: 'contents',
                color: getTextColor(color ?? 'primary'),
                fontSize: getFontSize(size ?? 'small'),
            }}
        >
            <Markdown value={source} renderer={renderer} />
        </Root>
    );
};

// marked-react re-parses on every render; every prop is a primitive, so shallow equality is exact.
export default React.memo(MarkdownBlock);
